import React, { useState } from 'react';

import classNames from 'classnames';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '@/components/ui/buttons/Button';
import Checkbox from '@/components/ui/forms/Checkbox';
import ErrorList from '@/components/ui/forms/ErrorList';
import Input from '@/components/ui/forms/Input';
import PasswordInput from '@/components/ui/forms/PasswordInput';
import useReactHookForm from '@/hooks/common/useReactHookForm';

import CSSModule from './LoginForm.module.scss';

interface FormValues {
  email_or_code: string;
  password: string;
  remember_me: boolean;
}

interface Props {
  isFetching: boolean;
  serverError: string | null;
  onSubmit: (values: FormValues) => void;
  serverResponseStatus?: number | null;
}

const LoginForm: React.FC<Props> = ({
  isFetching,
  serverError,
  onSubmit,
  serverResponseStatus,
}) => {
  const [formValues] = useState<FormValues>({
    email_or_code: '',
    password: '',
    remember_me: false,
  });

  const identifierLabel = 'メールアドレス';

  const validationSchema = Yup.object().shape({
    email_or_code: Yup.string().required(`${identifierLabel}は必須です`),
    password: Yup.string().required('パスワードは必須です'),
  });

  const {
    registerWithInnerRef,
    handleSubmit,
    formState: { isSubmitting, errorMessages: formErrorMessages },
  } = useReactHookForm<FormValues>({
    validationSchema,
    defaultValues: formValues,
  });

  const inputDisabled = isSubmitting || isFetching;
  const buttonDisabled = isSubmitting || isFetching;

  const serverErrorMessages = serverError
    ? serverResponseStatus === 422
      ? [
          'メールアドレスまたはパスワードが正しくありません',
          '大文字・小文字、全角・半角の違いもご確認ください',
          '2026/08/25から団体会員コードでのログインができなくなりました。メールアドレスでログインしてください',
        ]
      : [serverError]
    : [];

  const errorMessages = [
    ...(formErrorMessages.email_or_code || []),
    ...(formErrorMessages.password || []),
    ...serverErrorMessages,
  ];

  return (
    <form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onSubmit)}
      className={CSSModule.LoginForm}
    >
      <h2 className={CSSModule.LoginForm__Title}>保護者ログイン</h2>
      <div className={CSSModule.LoginForm__InputWrapper}>
        <Input
          {...registerWithInnerRef('email_or_code')}
          disabled={inputDisabled}
          placeholder={`${identifierLabel}を入力`}
        />
      </div>
      <div className={CSSModule.LoginForm__InputWrapper}>
        <PasswordInput
          {...registerWithInnerRef('password')}
          disabled={inputDisabled}
          placeholder="パスワードを入力"
        />
      </div>
      {errorMessages.length > 0 && <ErrorList errors={errorMessages} />}
      <div className={CSSModule.LoginForm__RememberMe}>
        <Checkbox
          {...registerWithInnerRef('remember_me')}
          disabled={inputDisabled}
        >
          ログインしたままにする
        </Checkbox>
      </div>
      <p className={CSSModule.LoginForm__NoteForSharedDevice}>
        共用の端末の場合はチェックを外してください
      </p>
      <Button
        color="primary"
        type="submit"
        disabled={buttonDisabled}
        className={CSSModule.LoginForm__LoginButton}
      >
        ログイン
      </Button>
      <Button
        color="outline"
        tag={Link}
        to="/registration"
        className={CSSModule.LoginForm__LinkToRegistration}
      >
        初めてご利用の方
      </Button>
      <div className={classNames(CSSModule.LoginForm__ForgotPageLinkWrapper)}>
        <Link to="/forgot" className={CSSModule.LoginForm__ForgotPageLink}>
          ログインにお困りの方はこちら
        </Link>
      </div>
    </form>
  );
};

export default LoginForm;
